import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { buildFunnelPageTree, buildFunnelPageTreePositions, getFunnelPageRelationships } from '../../../logic';
import type { FunnelPageItem, FunnelPageProductInfo } from '../../../types';
import {
    advanceJourney,
    computeEarnedCents,
    computeMaxRevenueCents,
    getJourneyCurrency,
    getPublishedPages,
    type JourneyStep,
} from './funnelJourney.logic';

export interface FunnelJourneyPreviewState {
    publishedPages: FunnelPageItem[];
    currentPage: FunnelPageItem | null;
    history: JourneyStep[];
    isComplete: boolean;
    earnedCents: number;
    maxRevenueCents: number;
    currency: string;
    progressPct: number;
    revenueJustIncreased: boolean;
    positionByPageId: Map<number, number>;
    relationshipByPageId: ReturnType<typeof getFunnelPageRelationships>;
    visitedIds: Set<number>;
    onBuy: () => void;
    onSkip: () => void;
    onRestart: () => void;
}

export function useFunnelJourneyPreview(
    pages: FunnelPageItem[],
    productInfoByPageId: Record<number, FunnelPageProductInfo | undefined>
): FunnelJourneyPreviewState {
    const publishedPages = useMemo(() => getPublishedPages(pages), [pages]);
    const firstPage = publishedPages[0] ?? null;

    const [currentPageId, setCurrentPageId] = useState<number | null>(firstPage?.page_id ?? null);
    const [anchorOrder, setAnchorOrder] = useState<number | null>(firstPage?.order ?? null);
    const [history, setHistory] = useState<JourneyStep[]>([]);
    const [isComplete, setIsComplete] = useState(false);
    const [revenueJustIncreased, setRevenueJustIncreased] = useState(false);
    const prevEarnedRef = useRef(0);

    const treeSlots = useMemo(() => buildFunnelPageTree(publishedPages), [publishedPages]);
    const positionByPageId = useMemo(() => buildFunnelPageTreePositions(treeSlots), [treeSlots]);
    const relationshipByPageId = useMemo(() => getFunnelPageRelationships(publishedPages), [publishedPages]);

    const currentPage = currentPageId !== null ? (publishedPages.find(p => p.page_id === currentPageId) ?? null) : null;

    const earnedCents = useMemo(() => computeEarnedCents(history, productInfoByPageId), [history, productInfoByPageId]);
    const maxRevenueCents = useMemo(
        () => computeMaxRevenueCents(publishedPages, productInfoByPageId),
        [publishedPages, productInfoByPageId]
    );
    const currency = useMemo(
        () => getJourneyCurrency(publishedPages, productInfoByPageId),
        [publishedPages, productInfoByPageId]
    );
    const progressPct = maxRevenueCents > 0 ? Math.round((earnedCents / maxRevenueCents) * 100) : 0;

    useEffect(() => {
        if (earnedCents > prevEarnedRef.current) {
            setRevenueJustIncreased(true);
            prevEarnedRef.current = earnedCents;
            const t = setTimeout(() => setRevenueJustIncreased(false), 600);
            return () => clearTimeout(t);
        }
        return undefined;
    }, [earnedCents]);

    const advance = useCallback(
        (action: 'buy' | 'skip') => {
            if (!currentPage) return;
            setHistory(prev => [...prev, { page: currentPage, action }]);
            const { page: next, anchorOrder: nextAnchor } = advanceJourney(
                publishedPages,
                currentPage.page_id,
                action,
                anchorOrder
            );
            setAnchorOrder(nextAnchor);
            if (next) {
                setCurrentPageId(next.page_id);
            } else {
                setCurrentPageId(null);
                setIsComplete(true);
            }
        },
        [currentPage, publishedPages, anchorOrder]
    );

    const onRestart = useCallback(() => {
        setCurrentPageId(firstPage?.page_id ?? null);
        setAnchorOrder(firstPage?.order ?? null);
        setHistory([]);
        setIsComplete(false);
        prevEarnedRef.current = 0;
    }, [firstPage?.page_id, firstPage?.order]);

    const onBuy = useCallback(() => advance('buy'), [advance]);
    const onSkip = useCallback(() => advance('skip'), [advance]);

    const visitedIds = useMemo(() => new Set(history.map(s => s.page.page_id)), [history]);

    return {
        publishedPages,
        currentPage,
        history,
        isComplete,
        earnedCents,
        maxRevenueCents,
        currency,
        progressPct,
        revenueJustIncreased,
        positionByPageId,
        relationshipByPageId,
        visitedIds,
        onBuy,
        onSkip,
        onRestart,
    };
}
